<template>
  <div class="c-field-preset-editor">
    <BittsLoading :is-loading="!ready || isLoadingPartnersUsingPreset">
      <div class="c-field-preset-editor__content">
        <!-- Preset Name Input -->
        <BittsInput
          v-model="presetName"
          type="text"
          placeholder="Enter name"
          name="preset-name"
          form-label="Preset name"
          :disabled="isCrossbeamPreset"
          size="small"
          class="max-w-[320px] mb-16"
          :status="hasDuplicateName ? 'danger' : 'default'"
          danger-text="Preset with this name already exists."
        />
        <BittsDivider class="my-0 mb-16" />

        <!-- Description Section -->
        <div v-if="!isDisabled" class="flex flex-col gap-4 mb-16">
          <h4 class="font-bold text-neutral-text-strong text-base m-0">
            Fields in this preset
          </h4>
          <p class="text-neutral-text text-sm m-0">
            Select which data fields are included in this preset. Presets are
            built by customizing Crossbeam's recommended fields, so you don't
            have to start from scratch.
          </p>
        </div>

        <!-- Alert for partners using preset -->
        <BittsAlert
          v-if="partnersUsingPreset && partnersUsingPreset.length > 0"
          color="warning"
          class="mb-8"
        >
          <template #body>
            <div class="flex items-center gap-8">
              <div class="text-sm text-warning-text">
                {{ presetUsageText }}
              </div>
              <BittsTooltip v-if="partnersUsingPreset.length > 0">
                <template #title>
                  {{
                    partnersUsingPreset
                      .map((partner: Partner) => partner.name)
                      .join(', ')
                  }}
                </template>
                <BittsAvatarStack :org-list="partnersUsingPreset" />
              </BittsTooltip>
            </div>
          </template>
        </BittsAlert>

        <FieldAndPresetSections
          :source-field-ids-by-mdm="sourceFieldIdsByMdm"
          :get-field-options="getFieldOptionsTyped"
          :disabled="isDisabled"
          :can-delete-preset="canDeletePreset"
          :has-partners-using-preset="!!partnersUsingPreset.length"
          @delete-preset="handleDeletePresetRequest"
          @navigate-to-sync-fields="navigateToSyncFields"
        />
      </div>
    </BittsLoading>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsAvatarStack,
  BittsDivider,
  BittsInput,
  BittsLoading,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { pluralize } from 'humanize-plus';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import FieldAndPresetSections from '@/components/field-presets/FieldAndPresetSections.vue';

import useAuth from '@/composables/useAuth';
import { useFieldPresets } from '@/composables/useFieldPresets';
import { usePartnersUsingPreset } from '@/composables/usePartnersUsingPreset';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { useDataSharePresetsStore, useFeedsStore } from '@/stores';
import { Partner } from '@/types/partners';
import { SourceMdm } from '@/types/sources';

import { DataSourceSettingsTabType } from '../data-sources/SettingsModal/constants';

type Props = {
  feedId: number;
  sourceId: number | undefined;
  presetId?: number;
  isCreatingNewPreset?: boolean;
  onSuccess?: (presetId: number, isNewPreset: boolean) => void;
  triggerAction?: number;
  triggerRestore?: number;
};

const props = withDefaults(defineProps<Props>(), {
  presetId: undefined,
  isCreatingNewPreset: false,
  onSuccess: undefined,
  triggerAction: 0,
  triggerRestore: 0,
});

const emit = defineEmits<{
  close: [];
  'footer-data': [
    data: {
      showRestoreButton: boolean;
      isRestoreDisabled: boolean;
      buttonText: string;
      isDisabled: boolean;
      isLoading: boolean;
    },
  ];
  'restore-default': [];
  action: [];
  'field-count-change': [count: number];
  'preset-name-change': [name: string];
  'action-complete': [];
  'restore-complete': [];
  'delete-preset-requested': [preset: { id: number; name: string }];
}>();

const { hasPermission } = useAuth();
const router = useRouter();

const feedsStore = useFeedsStore();
const dataSharePresetsStore = useDataSharePresetsStore();
const { ready, allPresets } = storeToRefs(dataSharePresetsStore);
const noNameBlocked = ref(false);

const scrollTarget = ref<HTMLDivElement>();

// Track original values to detect changes
const originalPresetName = ref<string>('');
const originalFieldIds = ref<number[]>([]);

const invalidPresetName = computed(
  () => !isToggledNewPreset.value && !presetName.value,
);

// Check if preset name already exists (excluding current preset when editing)
const hasDuplicateName = computed(() => {
  if (!presetName.value.trim()) return false;

  const normalizedName = presetName.value.trim().toLowerCase();

  return allPresets.value.some((preset) => {
    // Skip the current preset when editing
    if (props.presetId && preset.id === props.presetId) return false;

    // Only check presets for the same feed and source
    if (preset.feed_id !== props.feedId) return false;

    // Compare sourceId and base_source_id, treating null and undefined as equal
    const presetSourceId = preset.base_source_id ?? null;
    const currentSourceId = props.sourceId ?? null;
    if (presetSourceId !== currentSourceId) return false;

    // Check if name matches (case-insensitive)
    return preset.name?.trim().toLowerCase() === normalizedName;
  });
});

const hasPopulationPermission = computed(() =>
  hasPermission('manage:populations'),
);

const { partnersUsingPreset, isLoading: isLoadingPartnersUsingPreset } =
  usePartnersUsingPreset(props.presetId);

const {
  isLoading: isLoadingFieldPresets,
  presetName,
  isCrossbeamPreset,
  hasDefaultPresets,
  isToggledNewPreset,
  sourceFieldIdsByMdm,
  currentSourceFieldIds,
  createPreset,
  editPreset,
  getFieldOptions,
  resetToDefault,
} = useFieldPresets({
  feedId: props.feedId,
  sourceId: props.sourceId,
  presetId: props.presetId,
  isForPartner: false,
  eventSite: EVENT_SITES.DATA_PRESETS_TAB,
  onClose: () => {
    // Navigate back to list view when used in route context
    if (
      router.currentRoute.value.name === 'field-preset-new' ||
      router.currentRoute.value.name === 'field-preset-edit'
    ) {
      const { id, tab, source_id } = router.currentRoute.value.params;
      router.push({
        name: 'data-sources-settings-new',
        params: { id, tab, source_id },
      });
    } else {
      emit('close');
    }
  },
  onSuccess:
    props.onSuccess ||
    ((_presetId, _isNewPreset) => {
      // Navigate back to list view after successful create/edit when used in route context
      if (
        router.currentRoute.value.name === 'field-preset-new' ||
        router.currentRoute.value.name === 'field-preset-edit'
      ) {
        const { id, tab, source_id } = router.currentRoute.value.params;
        router.push({
          name: 'data-sources-settings-new',
          params: { id, tab, source_id },
        });
      }
    }),
});

const isDisabled = computed(
  () => isCrossbeamPreset.value || !hasPopulationPermission.value,
);

// Check if preset name or fields have changed
const hasChanges = computed(() => {
  // For new presets, always enable if name and fields are set
  if (props.isCreatingNewPreset || !props.presetId) {
    return (
      presetName.value.trim().length > 0 &&
      currentSourceFieldIds.value.length > 0
    );
  }

  // For existing presets, check if anything changed
  const nameChanged = presetName.value !== originalPresetName.value;
  const fieldsChanged =
    currentSourceFieldIds.value.length !== originalFieldIds.value.length ||
    currentSourceFieldIds.value.some(
      (id, index) => id !== originalFieldIds.value[index],
    );

  return nameChanged || fieldsChanged;
});

const hasNoFields = computed(() => currentSourceFieldIds.value.length === 0);

const canDeletePreset = computed(() => {
  return (
    !!props.presetId &&
    !isCrossbeamPreset.value &&
    hasPopulationPermission.value
  );
});

const presetUsageText = computed(
  () =>
    `${partnersUsingPreset.value.length} ${pluralize(partnersUsingPreset.value.length, 'partnership')} ${isDisabled.value ? 'using this preset' : 'will be impacted'}:`,
);

// Type-safe wrapper for getFieldOptions
const getFieldOptionsTyped = (
  mdmType: string,
):
  | {
      label: string | null;
      value: number | undefined;
    }[]
  | undefined => {
  return getFieldOptions(mdmType as SourceMdm);
};

// Button text - changes based on context
const buttonText = computed(() => {
  // Creating new preset from presets tab
  if (props.isCreatingNewPreset) {
    return 'Create preset';
  }
  // Saving existing population as new preset
  if (isToggledNewPreset.value) {
    return 'Create preset';
  }
  // Editing existing preset
  if (props.presetId) {
    return 'Save';
  }
  // Applying to population without saving
  return 'Apply';
});

// Exposed functions via events
function handleAction() {
  if (invalidPresetName.value) {
    noNameBlocked.value = true;
    scrollTarget.value?.scrollIntoView({ behavior: 'smooth' });
    return;
  }
  if (hasNoFields.value) return;
  props.presetId
    ? editPreset(props.presetId, presetName.value, currentSourceFieldIds.value)
    : createPreset(presetName.value, currentSourceFieldIds.value);
  emit('action');
}

function handleRestoreDefault() {
  resetToDefault();
  emit('restore-default');
}

// Watch for changes and emit footer data
watch(
  () => [
    ready.value,
    hasDefaultPresets.value,
    isDisabled.value,
    buttonText.value,
    isLoadingFieldPresets.value,
    isLoadingPartnersUsingPreset.value,
    hasChanges.value,
    hasNoFields.value,
    invalidPresetName.value,
    hasDuplicateName.value,
  ],
  () => {
    emit('footer-data', {
      showRestoreButton:
        ready.value && !hasDefaultPresets.value && !isDisabled.value,
      isRestoreDisabled: hasDefaultPresets.value || isDisabled.value,
      buttonText: buttonText.value,
      isDisabled:
        !ready.value ||
        isLoadingPartnersUsingPreset.value ||
        !hasChanges.value ||
        hasNoFields.value ||
        invalidPresetName.value ||
        hasDuplicateName.value,
      isLoading: !ready.value || isLoadingFieldPresets.value,
    });
  },
  { immediate: true },
);

// Watch for field count changes
watch(
  () => currentSourceFieldIds.value.length,
  (newCount) => {
    emit('field-count-change', newCount);
  },
  { immediate: true },
);

// Capture original values when preset loads (for edit mode)
watch(
  () => ready.value && presetName.value && currentSourceFieldIds.value.length,
  (isReady) => {
    if (isReady && props.presetId && originalPresetName.value === '') {
      originalPresetName.value = presetName.value;
      originalFieldIds.value = [...currentSourceFieldIds.value];
    }
  },
  { immediate: true },
);

// Watch for preset name changes
watch(
  () => presetName.value,
  (newName) => {
    emit('preset-name-change', newName);
  },
  { immediate: true },
);

// Watch for trigger action from parent (when used in router-view)
watch(
  () => props.triggerAction,
  (newValue) => {
    if (newValue && newValue > 0) {
      handleAction();
      emit('action-complete');
    }
  },
);

// Watch for trigger restore from parent (when used in router-view)
watch(
  () => props.triggerRestore,
  (newValue) => {
    if (newValue && newValue > 0) {
      handleRestoreDefault();
      emit('restore-complete');
    }
  },
);

function handleDeletePresetRequest() {
  if (!props.presetId) return;
  emit('delete-preset-requested', {
    id: props.presetId,
    name: presetName.value,
  });
}

async function navigateToSyncFields() {
  const feed = feedsStore.getFeedById(props.feedId);
  const type = feed?.integration.type;

  if (
    !type ||
    type === FILE_UPLOAD_DATA_SOURCE_TYPE ||
    type === GOOGLE_SHEETS_DATA_SOURCE_TYPE
  ) {
    await window.open(router.resolve({ name: 'data-sources' }).href, '_blank');
    return;
  }

  await router.push({
    name: 'data-sources-settings-new',
    params: { id: props.feedId, tab: DataSourceSettingsTabType.DATA_IMPORT },
  });
}

// Define the type for exposed properties
export type FieldPresetEditorExposed = {
  handleAction: () => void;
  handleRestoreDefault: () => void;
  presetName: typeof presetName;
  isCrossbeamPreset: typeof isCrossbeamPreset;
};

// Expose functions and data for parent component to call/access
defineExpose<FieldPresetEditorExposed>({
  handleAction,
  handleRestoreDefault,
  presetName,
  isCrossbeamPreset,
});
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";

.c-field-preset-editor {
  @apply flex flex-col h-full;
}

.c-field-preset-editor__content {
  @apply flex-1 overflow-y-auto;
}
</style>
